import type { BigSource } from 'big.js';
import styled from 'styled-components';
import { format } from '../../../../utils';
import { NumberVariants } from '../../../FormattedNumber';
import { useInlineFormattedNumberContext } from '../../../FormattedNumber/InlineFormattedNumberContext';

export interface DotAlignedPriceProps {
  number: BigSource;
  increment?: string;
  variant?: NumberVariants;
}

const Wrapper = styled.span<{ $variant?: NumberVariants }>`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  width: 100%;
  font-variant-numeric: tabular-nums;
  color: ${({ $variant, theme }) => {
    switch ($variant) {
      case NumberVariants.Positive:
        return theme.colorTextPositive;
      case NumberVariants.Negative:
        return theme.colorTextNegative;
      default:
        return 'inherit';
    }
  }};
`;

const IntPart = styled.span`
  min-width: 0;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DotPart = styled.span`
  /* sits in the auto-width centre column */
`;

const FracPart = styled.span`
  min-width: 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export function DotAlignedPrice({ number, increment, variant }: DotAlignedPriceProps) {
  const { showAllDecimals } = useInlineFormattedNumberContext();
  const formatted = format(number.toString(), { spec: increment, pretty: true, round: !showAllDecimals });

  const dotIndex = formatted.indexOf('.');
  const intPart = dotIndex === -1 ? formatted : formatted.slice(0, dotIndex);
  const fracPart = dotIndex === -1 ? '' : formatted.slice(dotIndex + 1);
  const hasDot = dotIndex !== -1;

  return (
    <Wrapper $variant={variant}>
      <IntPart>{intPart}</IntPart>
      <DotPart>{hasDot ? '.' : ''}</DotPart>
      <FracPart>{fracPart}</FracPart>
    </Wrapper>
  );
}
